import { useState, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import {
  InputText,
  Select,
  SelectListPosition,
  ButtonPseudo,
  ButtonSize,
  IconTrash,
} from '@ui/src';
import { useAppSelector } from '@store';
import { FeeAmountTypeEnum } from '../../hoc/tariffs/types';
import { FeeAttributeTypes } from './types';

import cn from './tariffs.module.css';

const FeeAttributes = ({
  control,
  errors,
  isEditable,
  feeId,
  writeOffType,
  manualFee,
  regularFee,
  feeCurrencyId,
  feeCascadeCurrenciesIds,
  setValue,
  clearErrors,
}: FeeAttributeTypes) => {
  const [cascadeCurrencies, setCascadeCurrencies] = useState<string[]>(['']);

  const { baseCurrency, currencies, currenciesList } = useAppSelector(
    (state) => state.configurationDictionaries,
  );

  useEffect(() => {
    if (feeCascadeCurrenciesIds && feeCascadeCurrenciesIds.length > 0) {
      setCascadeCurrencies(feeCascadeCurrenciesIds);
    }
  }, [feeCascadeCurrenciesIds]);

  let feeAmountContent;

  const currenciesWithoutBaseCurrency = currencies
    ?.filter((item) => item.value !== baseCurrency?.id)
    .sort((a, b) => a?.name?.localeCompare(b?.name));

  currenciesWithoutBaseCurrency?.unshift({
    name: baseCurrency?.description as string,
    value: baseCurrency?.id as string,
    suffix: baseCurrency?.name,
  });

  if (writeOffType === FeeAmountTypeEnum.PERCENT) {
    feeAmountContent = (
      <Controller
        control={control}
        name="feePercentAmount"
        render={({ field: { onChange, value, name } }) => (
          <InputText
            name={name}
            placeholder="Enter fee percentage"
            suffix="%"
            title="Write-off value"
            onChange={(e) =>
              onChange(e.target.value.replace(/[^0-9-\\.]/g, ''))
            }
            value={value}
            error={errors?.feePercentAmount?.message}
            className={cn.writeOffInput}
            disabled={Boolean(feeId && !isEditable)}
          />
        )}
      />
    );
  } else {
    feeAmountContent = (
      <Controller
        control={control}
        name="feeFixedAmount"
        render={({ field: { onChange, value, name } }) => (
          <InputText
            name={name}
            placeholder="Enter fee size"
            title="Write-off value"
            suffix={
              feeCurrencyId
                ? currencies?.find((item) => item.value === feeCurrencyId)
                    ?.suffix
                : baseCurrency?.name
            }
            onChange={(e) =>
              onChange(e.target.value.replace(/[^0-9-\\.]/g, ''))
            }
            value={value}
            error={errors?.feeFixedAmount?.message}
            className={cn.writeOffInput}
            disabled={Boolean(feeId && !isEditable)}
            currency={
              feeCurrencyId
                ? currenciesList?.find((item) => item.id === feeCurrencyId)
                : baseCurrency
            }
            amountInput
          />
        )}
      />
    );
  }

  return (
    <>
      <div className={cn.feeAttributeItem}>
        <Controller
          control={control}
          name="feeAmountType"
          defaultValue={undefined}
          render={({ field: { onChange, value, name } }) => (
            <>
              <Select
                name={name}
                title="Write-off type"
                list={
                  manualFee || regularFee
                    ? [
                        {
                          name: 'Fixed value',
                          value: FeeAmountTypeEnum.FIXED,
                        },
                      ]
                    : [
                        {
                          name: 'Percent of balance',
                          value: FeeAmountTypeEnum.PERCENT,
                        },
                        {
                          name: 'Fixed value',
                          value: FeeAmountTypeEnum.FIXED,
                        },
                      ]
                }
                onChange={(e) => {
                  onChange(e);
                  clearErrors('feeAmountType');
                }}
                placeholder="Select write-off type"
                value={value}
                selectWrapperClassName={`${cn.feeFormSelect} ${
                  errors?.feeAmountType?.message ? cn.feeFormSelectError : ''
                }`}
                disabled={Boolean(feeId && !isEditable)}
              />
              {errors?.feeAmountType?.message && (
                <div className={cn.feeFormSelectErrorText}>
                  {errors?.feeAmountType?.message}
                </div>
              )}
            </>
          )}
        />
      </div>
      {writeOffType && (
        <>
          <div className={cn.feeAttributeItem}>{feeAmountContent}</div>
          {writeOffType === FeeAmountTypeEnum.PERCENT && (
            <>
              <div className={cn.feeAttributeItem}>
                <Controller
                  control={control}
                  name="feePercentMinAmount"
                  render={({ field: { onChange, value, name } }) => (
                    <InputText
                      name={name}
                      title="Minimum amount allowed"
                      placeholder="Enter min amount allowed"
                      suffix={baseCurrency?.name}
                      onChange={(e) =>
                        onChange(e.target.value.replace(/[^0-9-\\.]/g, ''))
                      }
                      value={value}
                      error={errors?.feePercentMinAmount?.message}
                      className={cn.writeOffInput}
                      disabled={Boolean(feeId && !isEditable)}
                      currency={baseCurrency}
                      amountInput
                    />
                  )}
                />
              </div>
              <div className={cn.feeAttributeItem}>
                <Controller
                  control={control}
                  name="feePercentMaxAmount"
                  render={({ field: { onChange, value, name } }) => (
                    <InputText
                      name={name}
                      title="Maximum amount allowed"
                      placeholder="Enter max amount allowed"
                      onChange={(e) =>
                        onChange(e.target.value.replace(/[^0-9-\\.]/g, ''))
                      }
                      suffix={baseCurrency?.name}
                      className={cn.writeOffInput}
                      value={value}
                      error={errors?.feePercentMaxAmount?.message}
                      disabled={Boolean(feeId && !isEditable)}
                      currency={baseCurrency}
                      amountInput
                    />
                  )}
                />
              </div>
            </>
          )}
          {regularFee && (
            <>
              <div className={cn.feeAttributeItem}>
                <Controller
                  control={control}
                  name="feeCurrencyId"
                  render={({ field: { onChange, value, name } }) => (
                    <Select
                      name={name}
                      list={currenciesWithoutBaseCurrency || []}
                      title="Write-off currency"
                      placeholder="Select write-off currency"
                      onChange={onChange}
                      value={value}
                      error={errors?.feeCurrencyId?.message}
                      disabled={!isEditable}
                      selectListPosition={SelectListPosition.TOP}
                    />
                  )}
                />
              </div>
              <div className={cn.feeAttributeItem}>
                <div className={cn.cascadeCurrenciesTitle}>
                  Prioritization of currency selections
                </div>
                {cascadeCurrencies.map((item, index) => (
                  <div className={cn.cascadeCurrency} key={item}>
                    <Select
                      name={`cascade-currency-${item}`}
                      value={item}
                      className={cn.cascadeCurrencySelect}
                      list={
                        currenciesWithoutBaseCurrency?.map((currency) => ({
                          name: currency.name,
                          value: currency.value,
                          suffix: currency.suffix,
                          hidden: Boolean(
                            cascadeCurrencies?.find(
                              (cascadeCurrencyId) =>
                                cascadeCurrencyId === currency.value,
                            ),
                          ),
                        })) || []
                      }
                      selectListPosition={SelectListPosition.TOP}
                      onChange={(e) => {
                        let nextCurrencies = ([] as string[]).concat(
                          cascadeCurrencies,
                        );

                        if (cascadeCurrencies.length === 1) {
                          nextCurrencies = [e.target.value as string];
                        } else {
                          nextCurrencies[index] = e.target.value as string;
                        }

                        setCascadeCurrencies(nextCurrencies);
                        setValue('feeCascadeCurrenciesIds', nextCurrencies);
                      }}
                      title="Currency"
                      placeholder="Select currency"
                      fullWidth
                    />
                    <button
                      type="button"
                      aria-label="Delete"
                      className={cn.deleteCascadeCurrencyButton}
                      onClick={() => {
                        const nextCurrencies = cascadeCurrencies.filter(
                          (currency) => currency !== item,
                        );
                        setCascadeCurrencies(nextCurrencies);
                        setValue('feeCascadeCurrenciesIds', nextCurrencies);
                      }}
                    >
                      <IconTrash />
                    </button>
                  </div>
                ))}
                <ButtonPseudo
                  text="+ Add currency"
                  className={cn.addCurrencyButton}
                  size={ButtonSize.SMALL}
                  disabled={Boolean(
                    cascadeCurrencies.filter((item) => item === '').length,
                  )}
                  onClick={() => {
                    const nextCurrencies = ([] as string[]).concat(
                      cascadeCurrencies,
                    );

                    nextCurrencies.push('');

                    setCascadeCurrencies(nextCurrencies);
                  }}
                />
                <Controller
                  control={control}
                  name="feeCascadeCurrenciesIds"
                  render={({ field: { value, name } }) => (
                    <input type="hidden" value={value} name={name} />
                  )}
                />
              </div>
            </>
          )}
        </>
      )}
    </>
  );
};

export default FeeAttributes;
